import * as React from 'react'
import { Box } from '../box'
import RenderLoop from '../../anim/RenderLoop'
import * as utils from './utils'

/**
 * Offscreen canvas is used for rendering each individual partial weather element canvas, which is then
 * applied as an image for each icon as needed. This reduced the amount of rendering and calculations
 * required when multiple of the same element type is displayed.
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API/Tutorial/Optimizing_canvas
 * https://www.html5rocks.com/en/tutorials/canvas/performance/
 * https://dzone.com/articles/optimizing-a-canvas-animation
 */
class OffscreenCanvas {
	private canvas: HTMLCanvasElement
	private renderer: (context: any, time: number) => void

	public get context(): any {
		return this.canvas.getContext('2d')
	}

	public get image(): any {
		return this.canvas
	}

	constructor(width: number, height: number, renderer: (context: any, time: number) => void) {
		this.canvas = document.createElement('canvas')
		this.canvas.width = width
		this.canvas.height = height

		this.renderer = renderer

		if (this.renderer) {
			this.update(0)
		}
	}

	update(time: number) {
		this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)
		this.renderer(this.context, time)
	}
}

const STROKE = 0.08

const color = {
	main: '#222',
	moon: '#52526e',
	fog: '#CCC',
	fogbank: '#AAA',
	lightCloud: '#a9a9a9',
	lightCloudAlt: '#c1c1c1',
	cloud: '#666',
	darkCloud: '#444',
	thunder: '#ffc900',
	snow: '#99def7',
	hail: '#CCF',
	sleet: '#C2EEFF',
	wind: '#ccc',
	leaf: '#175810',
	rain: '#3082e6',
	freezingRain: '#43c4f2',
	sun: '#ff9d00',
	sunRay: '#FFDC00'
}

/**
 * x: horizontal position scale factor relative to the center
 * y: vertical position scale factor relative to the center
 * w: size scale factor
 * s: stroke width scale factor
 */
type DrawScale = {
	x?: number
	y?: number
	w?: number
	s?: number
}

const getCanvasSize = (ctx: any): any => {
	const w = ctx.canvas.width
    const h = ctx.canvas.height
	const s = Math.min(w, h)

	return { w, h, s }
}

const defaultDrawScale = { x: 0.5, y: 0.5, w: 1, s: 1 }
const getDrawOpts = (ctx: any, scale: DrawScale = {}, defaultScale: DrawScale = defaultDrawScale): utils.DrawOpts => {
	const { w, h, s } = getCanvasSize(ctx)
	scale = {...defaultScale, ...scale}
	scale.s = scale.s || 1

	return {
		cx: w * scale.x,
		cy: h * scale.y,
		w: s * scale.w,
		s: s * STROKE * scale.s
	}
}

type DrawDef = { partial: string, scale?: DrawScale, color?: string }

const drawScales = {
	sunMoonForCloud: { x: 0.667, y: 0.325, w: 0.65, s: 0.8 },
	cloudForSunMoon: { x: 0.375, y: 0.626, w: 0.75, s: 0.9 },
	cloudAltForSunMoon: { x: 0.75, y: 0.5, w: 0.6, s: 0.9 },
	cloudForPrecip: { x: 0.5, y: 0.37, w: 0.9, s: 1 },
	cloudForSunMoonPrecip: { x: 0.375, y: 0.5, w: 0.7, s: 0.9 },
	precipForSunMoon: { x: 0.375, y: 0.5, w: 0.7, s: 0.9 },
	lightningForSunMoon: { x: 0.375, y: 0.85, w: 0.7, s: 0.9 },
}

const drawingDefs: { [key: string]: DrawDef[]} = {
	clear: [{
		partial: 'sunmoon'
	}],
	partlyCloudy: [{
		partial: 'sunmoon',
		scale: drawScales.sunMoonForCloud
	},{
		partial: 'cloud',
		scale: drawScales.cloudForSunMoon
	}],
	mostlyCloudy: [{
		partial: 'sunmoon',
		scale: drawScales.sunMoonForCloud
	},{
		partial: 'cloud',
		scale: drawScales.cloudForSunMoon
	},{
		partial: 'cloud',
		scale: drawScales.cloudAltForSunMoon,
		color: color.lightCloudAlt
	}],
	cloudy: [{
		partial: 'cloud'
	}],
	scatteredShowers: [{
		partial: 'sunmoon',
		scale: drawScales.sunMoonForCloud
	},{
		partial: 'showers',
		scale: drawScales.precipForSunMoon
	},{
		partial: 'cloud',
		scale: drawScales.cloudForSunMoonPrecip
	}],
	showers: [{
		partial: 'showers'
	},{
		partial: 'cloud',
		scale: drawScales.cloudForPrecip
	}],
	drizzle: [{
		partial: 'drizzle'
	},{
		partial: 'cloud',
		scale: drawScales.cloudForPrecip
	}],
	rain: [{
		partial: 'rain'
	},{
		partial: 'cloud',
		scale: drawScales.cloudForPrecip,
		color: color.darkCloud
	}],
	scatteredStorms: [{
		partial: 'sunmoon',
		scale: drawScales.sunMoonForCloud
	},{
		partial: 'rain',
		scale: drawScales.precipForSunMoon
	},{
		partial: 'lightning',
		scale: drawScales.lightningForSunMoon
	},{
		partial: 'cloud',
		scale: drawScales.cloudForSunMoonPrecip,
		color: color.darkCloud
	}],
	storms: [{
		partial: 'rain'
	},{
		partial: 'lightning'
	},{
		partial: 'cloud',
		scale: drawScales.cloudForPrecip,
		color: color.darkCloud
	}],
	rainSnow: [{
		partial: 'rain'
	},{
		partial: 'snow'
	},{
		partial: 'cloud',
		scale: drawScales.cloudForPrecip
	}],
	freezingRain: [{
		partial: 'rain',
		color: color.freezingRain
	},{
		partial: 'cloud',
		scale: drawScales.cloudForPrecip
	}],
	sleet: [{
		partial: 'sleet'
	},{
		partial: 'cloud',
		scale: drawScales.cloudForPrecip
	}],
	wintryMix: [{
		partial: 'showers'
	},{
		partial: 'sleet'
	},{
		partial: 'snow'
	},{
		partial: 'cloud',
		scale: drawScales.cloudForPrecip
	}],
	sleetSnow: [{
		partial: 'sleet'
	},{
		partial: 'snow'
	},{
		partial: 'cloud',
		scale: drawScales.cloudForPrecip
	}],
	scatteredSnowShowers: [{
		partial: 'sunmoon',
		scale: drawScales.sunMoonForCloud
	},{
		partial: 'snow',
		scale: drawScales.precipForSunMoon
	},{
		partial: 'cloud',
		scale: drawScales.cloudForSunMoonPrecip
	}],
	snowFlurries: [{
		partial: 'flurries'
	},{
		partial: 'cloud',
		scale: drawScales.cloudForPrecip
	}],
	snowShowers: [{
		partial: 'snowShowers'
	},{
		partial: 'cloud',
		scale: drawScales.cloudForPrecip
	}],
	snow: [{
		partial: 'snow'
	},{
		partial: 'cloud',
		scale: drawScales.cloudForPrecip
	}],
	wind: [{
		partial: 'wind'
	}],
	fog: [{
		partial: 'fog'
	}],
	hazy: [{
		partial: 'sunmoon'
	},{
		partial: 'haze'
	}]
}

const renderLoop = new RenderLoop()
renderLoop.fps = 30		// drop the frame rate to improve performance!

const partialCanvasCache: { [key: string]: OffscreenCanvas } = {}

const render = (ctx: any, t: number, defs: DrawDef[], color: string | any, isNight: boolean = false, hasWind: boolean = false) => {
	if (!defs) return

	defs.forEach((def) => {
		let itemColor = color
		let renderer = drawing[def.partial]

		if (def.partial === 'sunmoon') {
			if (isNight) {
				renderer = drawing.moon
			} else {
				renderer = drawing.sun
			}
		}

		if (def.color) {
			itemColor = def.color
		}

		if (renderer) {
			renderer(ctx, t, itemColor, def.scale || {})
		}
	})

	if (hasWind) {
		drawing.wind(ctx, t, color)
	}
}

const staticPartials: string[] = []
const setupOffscreenDrawing = (defs: DrawDef[], color: string | any = null, isNight: boolean = false) => {
	if (!defs) return

	defs.forEach((def) => {
		let key = def.partial
		let itemColor = color
		let renderer = drawing[def.partial]

		if (def.partial === 'sunmoon') {
			if (isNight) {
				key = 'moon'
				renderer = drawing.moon
			} else {
				key = 'sun'
				renderer = drawing.sun
			}
		}

		if (def.color) {
			itemColor = def.color
			key = `${key}-${def.color.toLowerCase()}`
		}

		if (renderer) {
			let canvas = partialCanvasCache[key]
			if (!canvas) {
				canvas = new OffscreenCanvas(120, 120, (context: any, time: number) => {
					renderer(context, time, itemColor)
				})
				partialCanvasCache[key] = canvas

				if (staticPartials.indexOf(key) === -1) {
					renderLoop.add(key, canvas.context, (time: number) => {
						canvas.update(time)
					})
				}
			}
		}
	})
}

const drawing = {
	sun: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const opts = getDrawOpts(ctx, scale)
		utils.weather.sun(ctx, t, opts, color.sun || color, color.sunRay)
	},
	moon: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const opts = getDrawOpts(ctx, scale)
		utils.weather.moon(ctx, t, opts, color.moon || color)
	},
	cloud: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const opts = getDrawOpts(ctx, scale)
		utils.weather.cloud(ctx, t, opts, color.lightCloud || color)
	},
	drizzle: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const opts = getDrawOpts(ctx, scale, { x: 0.5, y: 0.37, w: 0.7, s: 1 })
		utils.weather.rain(ctx, t, opts, 2, color.rain || color)
	},
	showers: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const opts = getDrawOpts(ctx, scale, { x: 0.5, y: 0.37, w: 0.9, s: 1 })
		utils.weather.rain(ctx, t, opts, 4, color.rain || color)
	},
	rain: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const opts = getDrawOpts(ctx, scale, { x: 0.5, y: 0.37, w: 0.9, s: 1 })
		utils.weather.rain(ctx, t, opts, 12, color.rain || color)
	},
	sleet: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const opts = getDrawOpts(ctx, scale, { x: 0.5, y: 0.37, w: 0.9, s: 1 })
		utils.weather.sleet(ctx, t, opts, color.sleet || color)
	},
	flurries: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const opts = getDrawOpts(ctx, scale, { x: 0.5, y: 0.37, w: 0.7, s: 1 })
		utils.weather.snow(ctx, t, opts, 2, color.snow || color)
	},
	snowShowers: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const opts = getDrawOpts(ctx, scale, { x: 0.5, y: 0.37, w: 0.9, s: 1 })
		utils.weather.snow(ctx, t, opts, 4, color.snow || color)
	},
	snow: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const opts = getDrawOpts(ctx, scale, { x: 0.5, y: 0.37, w: 0.9, s: 1 })
		utils.weather.snow(ctx, t, opts, 12, color.snow || color)
	},
	lightning: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const opts = getDrawOpts(ctx, scale, { x: 0.5, y: 0.825, w: 0.9, s: 1 })
		utils.weather.lightning(ctx, t, opts, color.thunder || color)
	},
	wind: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const opts = getDrawOpts(ctx, scale, { x: 0.5, y: 0.5, w: 1, s: 1 })
		utils.weather.swoosh(ctx, t, opts, 0, 2, color.wind || color, color.leaf || color)
		utils.weather.swoosh(ctx, t, opts, 1, 2, color.wind || color, color.leaf || color)
	},
	fog: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const bankOpts = getDrawOpts(ctx, scale, { x: 0.5, y: 0.32, w: 0.75, s: 0.9 })
		const lineOpts = getDrawOpts(ctx, scale, { x: 1, y: 1, w: 1, s: 1 })
		utils.weather.fogbank(ctx, t, bankOpts, color.lightCloud || color)
		utils.weather.foglines(ctx, t, lineOpts, color.fog || color)
	},
	haze: (ctx: any, t: number, color: string | any, scale: DrawScale = null) => {
		const opts = getDrawOpts(ctx, scale, { x: 1, y: 1, w: 1, s: 1 })
		utils.weather.foglines(ctx, t, opts, color.fog || color)
	}
}

const nameMapping = {
	clear: ['sunny', 'clear', 'fair'],
	clearNight: ['sunnyn', 'clearn', 'fairn'],
	hazy: ['hazy', 'smoke', 'dust'],
	hazyNight: ['hazyn', 'smoken', 'dustn'],
	partlyCloudy: ['pcloudy'],
	partlyCloudyNight: ['pcloudyn'],
	mostlyCloudy: ['mcloudy'],
	mostlyCloudyNight: ['mcloudyn'],
	cloudy: ['cloudy'],
	drizzle: ['drizzle', 'drizzlef'],
	showers: ['showers'],
	rain: ['rain'],
	storms: ['tstorm', 'tstorms', 'tstormsw'],
	fog: ['fog', 'fogn'],
	wind: ['wind', 'cloudyw'],
	clearWind: ['sunnyw', 'clearw', 'fairw'],
	clearNightWind: ['sunnywn', 'clearwn', 'fairwn'],
	partlyCloudyWind: ['pcloudyw'],
	partlyCloudyNightWind: ['pcloudywn'],
	mostlyCloudyWind: ['mcloudyw'],
	mostlyCloudyNightWind: ['mcloudywn'],
	snowFlurries: ['snowflurries', 'flurries'],
	snowShowers: ['snowshowers'],
	snow: ['snow'],
	sleet: ['sleet', 'freezingrain'],
	sleetSnow: ['sleetsnow'],
	rainSnow: ['rainsnow', 'rainandsnow'],
	wintryMix: ['wintrymix'],
	scatteredShowers: ['pcloudyr', 'mcloudyr'],
	scatteredShowersNight: ['pcloudyrn', 'mcloudyrn'],
	scatteredSnowShowers: ['pcloudys', 'mcloudys'],
	scatteredSnowShowersNight: ['pcloudysn', 'mcloudysn'],
	mostlyCloudySnowFlurries: ['pcloudysf', 'mcloudysf'],
	mostlyCloudySnowFlurriesNight: ['pcloudysfn', 'mcloudysfn'],
	scatteredStorms: ['pcloudyt', 'mcloudyt'],
	scatteredStormsNight: ['pcloudytn', 'mcloudytn'],
	rainWind: ['rainw'],
	showersWind: ['showersw'],
	snowWind: ['blizzard', 'snoww', 'blowingsnow'],
	snowShowersWind: ['snowshowersw'],
	scatteredShowersWind: ['pcloudyrw', 'mcloudyrw'],
	scatteredShowersNightWind: ['pcloudyrwn', 'mcloudyrwn'],
	scatteredSnowShowersWind: ['pcloudysw', 'mcloudysw'],
	scatteredSnowShowersNightWind: ['pcloudyswn', 'mcloudyswn'],
	mostlyCloudySnowFlurriesWind: ['pcloudysfw', 'mcloudysfw'],
	mostlyCloudySnowFlurriesWindNight: ['pcloudysfwn', 'mcloudysfwn'],
	scatteredStormsWind: ['pcloudytw', 'mcloudytw'],
	scatteredStormsNightWind: ['pcloudytwn', 'mcloudytwn']
}

type WeatherIconProps = any & {
	name?: string
	code?: string
	width?: number
	height?: number
}

type WeatherIconState = {
	width: number
	height: number
}

class WeatherIcon extends React.Component<WeatherIconProps, WeatherIconState> {
	static defaultProps = {
		width: 50,
		height: 50
	}

	id: string
	code: string
	context: any
	resizeClear: boolean = false
	drawingFn: (ctx: any, t: number, color: string | any, isNight: boolean, hasWind: boolean) => void

	private drawingDefs: DrawDef[];
	private elements: string[]

	constructor(props: any) {
		super(props)

		this.state = {
			width: 40,
			height: 40
		}
	}

	componentDidMount() {
		const { name, code } = this.props
		this.code = name || code

		if (!this.code) {
			throw new Error('Invalid configuration for WeatherIcon, `name` or `code` were not provided.');
		}

		// set canvas size
		if (this.context) {
			const container = this.context.canvas.parentElement
			if (container) {
				const bounds = container.getBoundingClientRect()
				this.setState({
					width: bounds.width,
					height: bounds.height
				})
			}
		}

		// if code was provided instead of name, then iterate through the mappings dictionary
		// to find the name that matches the provided weather icon code
		if (!name && code) {
			let iconCode = code;

			// strip off "n" suffix if it doesn't contain night-specific imagery
			let reSunMoon = new RegExp('^(sunny|clear|fair|pcloudy(r|s|sf|t)?|mcloudy(r|s|sf|t)?|hazy|wind|smoke|dust)w?n?$');
			if (!/rain$/.test(iconCode) && !reSunMoon.test(iconCode)) {
				iconCode = iconCode.replace(/n$/, '');
			}

			Object.keys(nameMapping).forEach((key) => {
				const patterns = nameMapping[key]
				if (patterns) {
					patterns.forEach((pattern) => {
						pattern = `^${pattern}$`
						const regex = new RegExp(pattern)
						if (regex.test(iconCode)) {
							this.code = key
						}
					})
				}
			})
		}

		const key = this.code.replace(/(Night)?(Wind)?$/, '')
		const isNight = /Night(Wind)?$/.test(this.code)
		const hasWind = /Wind$/.test(this.code)

		if (drawingDefs[key]) {
			this.drawingDefs = drawingDefs[key]
			setupOffscreenDrawing(drawingDefs[key], color, isNight)

			if (hasWind) {
				setupOffscreenDrawing(drawingDefs.wind, color)
			}

			if (this.context) {
				renderLoop.add(null, this.context, (time: number) => {
					this.draw()
				})
			}
		}
	}

	draw(time: number = 0) {
		if (!this.context.canvas) { return }

		const canvas = this.context.canvas
		const isNight = /Night(Wind)?$/.test(this.code)
		const hasWind = /Wind$/.test(this.code)

		if (this.resizeClear) {
			canvas.width = canvas.width
		} else {
			this.context.clearRect(0, 0, canvas.width, canvas.height)
		}

		const drawing = this.drawingDefs
		if (drawing) {
			drawing.forEach((def) => {
				let key = def.partial

				if (key === 'sunmoon') {
					key = isNight ? 'moon' : 'sun'
				}

				if (def.color) {
					key = `${key}-${def.color.toLowerCase()}`
				}

				const cached = partialCanvasCache[key]
				const cachedWind = partialCanvasCache.wind

				if (cached && cached.image) {
					const image = cached.image
					const scale = def.scale || defaultDrawScale
					const dest = {
						x: 0,
						y: 0,
						width: canvas.width,
						height: canvas.height
					}

					if (scale) {
						dest.width *= scale.w
						dest.height *= scale.w
						dest.x = (canvas.width * scale.x) - dest.width / 2
						dest.y = (canvas.height * scale.y) - dest.height / 2
					}

					Object.keys(dest).forEach((key) => {
						let val = Math.round(dest[key])
						if (key === 'x' || key === 'y') {
							val = Math.max(0, val)
						}
						dest[key] = val
					})

					this.context.drawImage(image, 0, 0, image.width, image.height, dest.x, dest.y, dest.width, dest.height)

					if (hasWind && cachedWind) {
						const windImage = cachedWind.image
						this.context.drawImage(windImage, 0, 0, windImage.width, windImage.height, 0, 0, canvas.width, canvas.height)
					}

				} else {
					console.log('no cached canvas for key', key)
				}
			})
		}
	}

	render() {
		const props = {...this.props}

		if (!props.width) {
			props.width = '60px'
		}
		if (!props.height) {
			props.height = '60px'
		}

		return (
			<Box {...props}>
				<canvas
					ref={(c) => { if (c) this.context = c.getContext('2d') }}
					width={this.state.width}
					height={this.state.height}
				></canvas>
			</Box>
		)
	}
}

export default WeatherIcon
